// src/store/slices/billingSlice2.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

export interface SectionData {
    name: string;
    company: string;
    email: string;
    phone?: string;
    address: string;
}

export interface BillingState {
    billingDetails: SectionData;
    contactDetails: SectionData;
    labelReceiptsAsInvoices: boolean;
    billEstimates: boolean;
    loading: boolean;
    updating: boolean;
    error: string | null;
}

const initialState: BillingState = {
    billingDetails: {
        name: '',
        company: '',
        email: '',
        address: '',
    },
    contactDetails: {
        name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
    },
    labelReceiptsAsInvoices: false,
    billEstimates: false,
    loading: false,
    updating: false,
    error: null,
};

const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
};

// Async thunk for fetching billing profile
export const fetchBillingProfile2 = createAsyncThunk(
    'billing2/fetchProfile',
    async (_, { rejectWithValue }) => {
        try {
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                throw new Error('Authentication required');
            }

            const idempotencyKey = generateIdempotencyKey();

            const response = await fetch(`${BASE_URL}/api/settings/billing/profile`, {
                method: 'GET',
                headers: {
                    'X-Idempotency-Key': idempotencyKey,
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch billing profile: ${response.status}`);
            }

            const data = await response.json();

            if (!data || !data.data) {
                throw new Error('Invalid response format');
            }

            return data.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load billing profile');
        }
    }
);

// Async thunk for updating billing profile
export const updateBillingProfile2 = createAsyncThunk(
    'billing2/updateProfile',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { billing2: BillingState };
            const { billingDetails, contactDetails, labelReceiptsAsInvoices, billEstimates } = state.billing2;

            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                throw new Error('Authentication required');
            }

            const idempotencyKey = generateIdempotencyKey();

            const requestBody = {
                billingName: billingDetails.name,
                billingCompany: billingDetails.company,
                billingEmail: billingDetails.email,
                billingAddress: billingDetails.address,
                contactName: contactDetails.name,
                contactEmail: contactDetails.email,
                contactPhone: contactDetails.phone || '',
                contactCompany: contactDetails.company,
                labelReceiptsAsInvoices: labelReceiptsAsInvoices,
                billEstimates: billEstimates,
            };

            const response = await fetch(`${BASE_URL}/api/settings/billing/profile`, {
                method: 'PATCH',
                headers: {
                    'X-Idempotency-Key': idempotencyKey,
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage = `Failed to update billing profile: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    errorMessage = response.statusText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            return data.data || requestBody;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update billing profile');
        }
    }
);

const billingSlice2 = createSlice({
    name: 'billing2',
    initialState,
    reducers: {
        updateBillingDetails2: (state, action: PayloadAction<SectionData>) => {
            state.billingDetails = action.payload;
        },
        updateContactDetails2: (state, action: PayloadAction<SectionData>) => {
            state.contactDetails = action.payload;
        },
        updateLabelReceiptsAsInvoices2: (state, action: PayloadAction<boolean>) => {
            state.labelReceiptsAsInvoices = action.payload;
        },
        updateBillEstimates2: (state, action: PayloadAction<boolean>) => {
            state.billEstimates = action.payload;
        },
        clearBillingError2: (state) => {
            state.error = null;
        },
        resetBillingState2: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // Fetch billing profile
            .addCase(fetchBillingProfile2.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBillingProfile2.fulfilled, (state, action) => {
                state.loading = false;
                const data = action.payload;
                state.billingDetails = {
                    name: data.billingName || '',
                    company: data.billingCompany || '',
                    email: data.billingEmail || '',
                    address: data.billingAddress || '',
                };
                state.contactDetails = {
                    name: data.contactName || '',
                    company: data.contactCompany || '',
                    email: data.contactEmail || '',
                    phone: data.contactPhone || '',
                    address: data.billingAddress || '',
                };
                state.labelReceiptsAsInvoices = data.labelReceiptsAsInvoices || false;
                state.billEstimates = data.billEstimates || false;
            })
            .addCase(fetchBillingProfile2.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || 'Failed to load billing profile';
            })
            // Update billing profile
            .addCase(updateBillingProfile2.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(updateBillingProfile2.fulfilled, (state, action) => {
                state.updating = false;
                const data = action.payload;
                if (data) {
                    state.billingDetails = {
                        name: data.billingName || state.billingDetails.name,
                        company: data.billingCompany || state.billingDetails.company,
                        email: data.billingEmail || state.billingDetails.email,
                        address: data.billingAddress || state.billingDetails.address,
                    };
                    state.contactDetails = {
                        name: data.contactName || state.contactDetails.name,
                        company: data.contactCompany || state.contactDetails.company,
                        email: data.contactEmail || state.contactDetails.email,
                        phone: data.contactPhone || state.contactDetails.phone,
                        address: data.billingAddress || state.contactDetails.address,
                    };
                    state.labelReceiptsAsInvoices = data.labelReceiptsAsInvoices !== undefined ? data.labelReceiptsAsInvoices : state.labelReceiptsAsInvoices;
                    state.billEstimates = data.billEstimates !== undefined ? data.billEstimates : state.billEstimates;
                }
            })
            .addCase(updateBillingProfile2.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload as string || 'Failed to update billing profile';
            });
    },
});

export const {
    updateBillingDetails2,
    updateContactDetails2,
    updateLabelReceiptsAsInvoices2,
    updateBillEstimates2,
    clearBillingError2,
    resetBillingState2,
} = billingSlice2.actions;

export default billingSlice2.reducer;